import { FC, useEffect, useRef } from 'react';
import { useState } from 'react';
import PropTypes, { element } from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  MenuItem,
  Select,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  Typography,
  Divider,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Tooltip,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  ButtonGroup,
  Box
} from '@material-ui/core';
import { useDispatch, useSelector } from "react-redux";
import type { Customer } from '../../types/customer';
import { InputField } from '../input-field';
import { AutocompleteField } from '../autocomplete-field';
import { customerPostOne, customerPutOne, customerDeleteOne } from '../../api/review-1/review-1-customer';
import { XCircle as XCircleIcon } from '../../icons/x-circle';
import { ConfirmationDialog } from '../confirmation-dialog';
import { concernings, filterActiveConcernings } from '../../utils/concernings';
import { review3GetAll } from '../../slices/review3Slice';
import { factorPost, resetFactorStatus, factorDelete, addSelectedConcerning, removeSelectedConcerning, clearSelectedConcernings, addSelectedPreventiveMove, removeSelectedPreventiveMove, clearSelectedPreventiveMoves, addSelectedContingencyMove, removeSelectedContingencyMove, clearSelectedContingencyMoves, addSelectedBoosterMove, removeSelectedBoosterMove, clearSelectedBoosterMoves } from '../../slices/factorSlice';
import { RootState } from '../../store';
import { useTheme } from "@material-ui/core/styles";
import { useTranslation } from 'react-i18next';
import { InfoPop } from '../info-pop';
import { PopoverConcerning } from '../popover-concerning';
import { openFactorDialog } from 'src/slices/factorSlice';
import { ChevronLeft as ChevronLeftIcon } from '../../icons/chevron-left';
import { ChevronRight as ChevronRightIcon } from '../../icons/chevron-right';
import { ChevronDown as ChevronDownIcon } from '../../icons/chevron-down';
import { factorGetAll } from 'src/api/factor';
import { TableItemMenu } from '../table-item-menu';
import { customerGetAll } from '../../api/review-1/review-1-customer';
import { companyGetAll } from '../../api/review-1/review-1-company';
import { FactorPreventiveMovesPanel } from './factor-preventive-moves-panel';
import { FactorContingencyMovesPanel } from './factor-contingency-moves-panel';
import { FactorBoosterMovesPanel } from './factor-booster-moves-panel';
import FactorConcerningsPanel from './factor-concernings-panel';
import { DeactivatableField } from 'src/components/deactivatable-field';
import { ConfidenceTabItem } from '../general/confidence-tab-item';
import { CollapsibleSection } from '../collapsible-section';
import { SectionSaveActions } from '../section-save-actions';

interface FactorDialogProps {
  open: boolean;
  onClose: () => void;
}

// Importance scale reused from the Benchmarking / stakeholder-lever importance
// dropdown so the Factor importance matches them visually and value-wise.
const importanceStatus = {
  0: {
    label: '?',
    color: '#8A8A8A',
    backgroundColor: 'rgba(138, 138, 138, 0.05)'
  },
  2: {
    label: 'not_important',
    color: '#AA9F3D',
    backgroundColor: 'rgba(255, 236, 68, 0.27)'
  },
  3: {
    label: 'important',
    color: '#D7AC3D',
    backgroundColor: 'rgba(255, 203, 68, 0.27)'
  },
  4: {
    label: 'very_important',
    color: '#FF7A00',
    backgroundColor: 'rgba(255, 122, 0, 0.27)'
  },
  5: {
    label: 'critical',
    color: '#FF2200',
    backgroundColor: 'rgba(255, 34, 0, 0.27)'
  }
};

export const FactorDialog: FC<FactorDialogProps> = (props) => {
  const { t } = useTranslation();
  const { open, onClose, ...other } = props;
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  // const { factorIsLoading, factorStatus, factorNextAction } = useSelector((state: RootState) => state.factor);
  const factor = useSelector((state: RootState) => state.factor);
  const { factors } = useSelector((state: RootState) => state.review3);
  const { factorIsLoading, factorStatus, factorNextAction, selectedConcernings, selectedPreventiveMoves, selectedContingencyMoves, selectedBoosterMoves } = useSelector((state: RootState) => state.factor);
  const [showList, setShowList] = useState(true);
  const [showConcerningsPanel, setShowConcerningsPanel] = useState(false);
  const [showPreventiveMovesPanel, setShowPreventiveMovesPanel] = useState(false);
  const [showContingencyMovesPanel, setShowContingencyMovesPanel] = useState(false);
  const [showBoosterMovesPanel, setShowBoosterMovesPanel] = useState(false);
  const [clients, setClients] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [searchedString, setSearchedString] = useState('');

  const [height, setHeight] = useState(0)
  const mainGridRef = useRef(null)
  const [refVisible, setRefVisible] = useState(false)

  const anchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!refVisible) {
      return
    }
    setHeight(mainGridRef.current.clientHeight)
  }, [refVisible, mainGridRef?.current?.clientHeight])

  useEffect(() => {
    getItems();
  }, []);

  useEffect(() => {
    getItems();
  }, [searchedString]);

  // Load clients and companies for chip display
  useEffect(() => {
    const loadClientsAndCompanies = async () => {
      try {
        const [clientsData, companiesData] = await Promise.all([
          customerGetAll(),
          companyGetAll()
        ]);

        console.log('Loaded clients data:', clientsData);
        console.log('Loaded companies data:', companiesData);

        if (clientsData !== 'error') {
          setClients(clientsData || []);
        }

        if (companiesData && !companiesData.err) {
          setCompanies(companiesData.items || []);
        }

        setDataLoaded(true);
      } catch (error) {
        console.error('Error loading clients and companies:', error);
        setDataLoaded(true);
      }
    };

    loadClientsAndCompanies();
  }, []);

  // Function to get display name for a concerning
  const getConcerningDisplayName = (concerningId: string | any) => {
    console.log('Getting display name for:', concerningId);
    console.log('Available clients:', clients);
    console.log('Available companies:', companies);

    // Handle case where concerningId is an object (from API)
    if (typeof concerningId === 'object' && concerningId !== null) {
      // If it's an object with a value property, use that
      if (concerningId.value) {
        return t(concernings[concerningId.value]?.label || concerningId.value);
      }
      // If it's an object with an id property, convert to string
      if (concerningId.id) {
        concerningId = concerningId.id.toString();
      } else {
        // Fallback for unknown object structure
        return t('Unknown concerning');
      }
    }

    // Ensure concerningId is a string
    const concerningIdStr = String(concerningId);

    // Check if it's a client
    if (concerningIdStr.startsWith('clients_')) {
      const clientId = concerningIdStr.replace('clients_', '');
      console.log('Looking for client with ID:', clientId);
      const client = clients.find(c => c.id == clientId || c.id === parseInt(clientId));
      console.log('Found client:', client);
      return client ? (client.name || client.description || `Client ${clientId}`) : concerningIdStr;
    }

    // Check if it's a company
    if (concerningIdStr.startsWith('companies_')) {
      const companyId = concerningIdStr.replace('companies_', '');
      console.log('Looking for company with ID:', companyId);
      const company = companies.find(c => c.id == companyId || c.id === parseInt(companyId));
      console.log('Found company:', company);
      return company ? (company.name || company.description || `Company ${companyId}`) : concerningIdStr;
    }

    // For static concernings, use the concernings mapping
    return t(concernings[concerningIdStr]?.label || concerningIdStr);
  };

  const getItems = async () => {
    const res = await factorGetAll('description', 'asc', searchedString, 1, 1000);
    console.log(res);
    if (res.items) {
      setItems(res.items);
      setTotal(res.total);
    } else {
      setItems([]);
      setTotal(0);
    }
  };

  const dispatch = useDispatch();

  let initialValues = {
    description: '',
    detailed_description: '',
    comment: '',
    concernings: [],
    positiveImpact: -1,
    negativeImpact: -1,
    opportunityKiller: 0,
    probability: 0,
    confidence_indice: 1,
    order: null,
    submit: null
  };

  if ((factor.factorId !== null && factor.factorId !== undefined) || factor.isDuplicated === true) {
    console.log('factor id', factor.factorId);
    initialValues.description = factor.description;
    initialValues.detailed_description = factor.detailedDescription;
    initialValues.comment = factor.comment;
    initialValues.concernings = factor.concernings;
    initialValues.positiveImpact = factor.positiveImpact;
    initialValues.negativeImpact = factor.negativeImpact;
    initialValues.opportunityKiller = factor.opportunityKiller;
    initialValues.probability = factor.probability;
    initialValues.confidence_indice = factor.confidenceIndice;
  }

  useEffect(() => {
    console.log('store status', factor.factorIsLoading);
    if (factor.factorStatus === 'success') {
      toast.success(`Factor created`);
      dispatch(review3GetAll());
      formik.resetForm();
      formik.setStatus({ success: true });
      formik.setSubmitting(false);
      dispatch(resetFactorStatus());
      getItems(); // Refresh the factors list in the right panel
      if (factor.factorNextAction === 'close') {
        onClose();
      } else {
        // "Save and create new": reset the dialog to a blank new factor.
        dispatch(openFactorDialog({ element: { id: null }, isEditabled: true, isDuplicated: false }));
      }
    } else {
      formik.setStatus({ success: false });
      formik.setErrors({ submit: factor.factorStatus });
      formik.setSubmitting(false);
    }
  }, [factor.factorStatus]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    validationSchema: Yup.object().shape({
      description: Yup.string().min(3).max(255).required('Description is required'),
    }),
    onSubmit: async (values, helpers) => {
      try {
        // dispatch(review1PostCustomer(values));
      } catch (err) {
        console.error(err);
        helpers.setStatus({ success: false });
        helpers.setErrors({ submit: err.message });
        helpers.setSubmitting(false);
      }
    }
  });

  const handlePositiveImpact = (event: React.MouseEvent<HTMLElement>, newAlignment: number | null) => {
    formik.setFieldValue('positiveImpact', newAlignment);
  };

  const handleNegativeImpact = (event: React.MouseEvent<HTMLElement>, newAlignment: number | null) => {
    formik.setFieldValue('negativeImpact', newAlignment);
  };

  const handleOpportunityKiller = (event: React.ChangeEvent<HTMLInputElement>) => {
    // setChecked([checked[0], event.target.checked]);
    formik.setFieldValue('opportunityKiller', event.target.checked);
  };

  const handleSubmitAndQuit = () => {
    console.log('Submit and quit');
    console.log('selectedPreventiveMoves:', selectedPreventiveMoves);
    console.log('selectedContingencyMoves:', selectedContingencyMoves);
    const dataWithMoves = {
      ...formik.values,
      selectedPreventiveMoves: selectedPreventiveMoves,
      selectedContingencyMoves: selectedContingencyMoves,
      selectedBoosterMoves: selectedBoosterMoves,
      concernings: selectedConcernings.map(concerning => {
        // If concerning is already an object with id and value properties, use it as-is
        if (typeof concerning === 'object' && concerning !== null && concerning.id && concerning.value) {
          return concerning;
        }
        // Otherwise, wrap it in the expected format
        return {
          value: concerning,
          id: concerning
        };
      })
    };
    console.log('dataWithMoves being sent:', dataWithMoves);

    // Validate move IDs before sending
    if (dataWithMoves.selectedPreventiveMoves && dataWithMoves.selectedPreventiveMoves.length > 0) {
      console.log('Validating preventive move IDs:');
      dataWithMoves.selectedPreventiveMoves.forEach((move, index) => {
        console.log(`  Move ${index}:`, move);
        if (move.id) {
          console.log(`    ID: ${move.id}, Valid UUID: ${/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(move.id)}`);
        }
      });
    }

    if (dataWithMoves.selectedContingencyMoves && dataWithMoves.selectedContingencyMoves.length > 0) {
      console.log('Validating contingency move IDs:');
      dataWithMoves.selectedContingencyMoves.forEach((move, index) => {
        console.log(`  Move ${index}:`, move);
        if (move.id) {
          console.log(`    ID: ${move.id}, Valid UUID: ${/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(move.id)}`);
        }
      });
    }

    dispatch(factorPost('close', dataWithMoves, factor.factorId));
  };

  const handleSubmitAndReset = () => {
    console.log('Submit and reset');
    const dataWithMoves = {
      ...formik.values,
      selectedPreventiveMoves: selectedPreventiveMoves,
      selectedContingencyMoves: selectedContingencyMoves,
      selectedBoosterMoves: selectedBoosterMoves,
      concernings: selectedConcernings.map(concerning => {
        // If concerning is already an object with id and value properties, use it as-is
        if (typeof concerning === 'object' && concerning !== null && concerning.id && concerning.value) {
          return concerning;
        }
        // Otherwise, wrap it in the expected format
        return {
          value: concerning,
          id: concerning
        };
      })
    };

    // Validate move IDs before sending
    if (dataWithMoves.selectedPreventiveMoves && dataWithMoves.selectedPreventiveMoves.length > 0) {
      console.log('Validating preventive move IDs:');
      dataWithMoves.selectedPreventiveMoves.forEach((move, index) => {
        console.log(`  Move ${index}:`, move);
        if (move.id) {
          console.log(`    ID: ${move.id}, Valid UUID: ${/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(move.id)}`);
        }
      });
    }

    if (dataWithMoves.selectedContingencyMoves && dataWithMoves.selectedContingencyMoves.length > 0) {
      console.log('Validating contingency move IDs:');
      dataWithMoves.selectedContingencyMoves.forEach((move, index) => {
        console.log(`  Move ${index}:`, move);
        if (move.id) {
          console.log(`    ID: ${move.id}, Valid UUID: ${/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(move.id)}`);
        }
      });
    }

    dispatch(factorPost('new', dataWithMoves));
  };

  const handleUpdateConcernings = (newConcernings: any[]) => {
    formik.setFieldValue('concernings', newConcernings);
  };

  // Every Save button (top block + both sections) persists the full current
  // form state via the same submit path, so a section save never requires the
  // other sections to be valid. The only gate remains the compulsory name field.
  const saveDisabled = (factor.factorIsLoading || !factor.isEditabled || (formik.values.description.length < 3));
  const saveLabel = (factor.factorId !== null && factor.factorId !== undefined) ? t('update') : t('create');

  return (
    <Dialog
      maxWidth="xl"
      fullWidth
      onClose={() => { formik.values.description !== '' ? setOpenConfirmationDialog(true) : onClose(); }}
      open={open}
      TransitionProps={{
        onExited: () => formik.resetForm()
      }}
      PaperProps={{
        style: { borderRadius: 25 }
      }}
      {...other}
    >
      {/* <DialogTitle>
        {(factor.factorId !== null && factor.factorId !== undefined) ? `${t('update_factor')} : ${factor.description}` : t('create_factor')}
        <div style={{ display: 'inline-block'}}>
          <InfoPop
            infoElementName={t('factors')}
            shortText='What is a Factor?'
            position='right'
            longText='Factors are those events, circumstances or conditions generally outside your sphere of influence that can influence the ability to achieve the Definition of Success. <br />
            Boosters are Factors that can contribute to success, while Hurdles are those that will slow down the Project or even block it (Obstacles). <br />
            Apart from the Factors whose probability is certain (probability = 100%), the other Factors have a probability of occurrence that is less than 100%. <br />
            To indicate the level of probability of uncertain Factors, they can be grouped as "low probability", "medium probability" or "high probability".<br />
            Risks are Hurdles with a probability of occurrence of less than 100%.'
          />
        </div>
      </DialogTitle> */}
      <DialogContent sx={{ padding: 0, backgroundColor: "background.default" }}>
        <Grid
          container
          spacing={2}
        // my={2}
        >
          <Grid item xs={showList ? 8 : 12} sx={{ p: 0, backgroundColor: "background.default" }}>
            <Grid container spacing={2} padding={4} sx={{ overflowY: "scroll" }} maxHeight={'80vh'} ref={el => { mainGridRef.current = el; setRefVisible(!!el); }}>
              <Grid item xs={12}>
                <Typography variant="h5" color={'warning.main'}>
                  {(factor.factorId !== null && factor.factorId !== undefined) ? `${t('update_factor')} : ${factor.description}` : t('create_factor')}
                  <div style={{ display: 'inline-block', textAlign: 'right', float: 'right' }}>
                    <IconButton onClick={() => setShowList(!showList)}>
                      {showList ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                    </IconButton>
                  </div>
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
              >
                <DeactivatableField review={3} screen="factors" field="description">
                  <InputField
                    autoFocus
                    error={Boolean(formik.touched.description && formik.errors.description)}
                    fullWidth
                    helperText={formik.touched.description && formik.errors.description}
                    label={t('factor_name')}
                    name="description"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    required
                    type="description"
                    value={formik.values.description}
                    multiline
                  />
                </DeactivatableField>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
              >
                <DeactivatableField review={3} screen="factors" field="detailed_description">
                  <InputField
                    fullWidth
                    label={t('description_of_this_factor')}
                    name="detailed_description"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.detailed_description}
                    multiline
                    rows={2}
                  />
                </DeactivatableField>
              </Grid>

              {/* Concernings Section */}
              <Grid item xs={12}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1
                }}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {t('Concerning')}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      if (showConcerningsPanel) {
                        setShowConcerningsPanel(false);
                      } else {
                        setShowPreventiveMovesPanel(false);
                        setShowContingencyMovesPanel(false);
                        setShowBoosterMovesPanel(false);
                        setShowConcerningsPanel(true);
                      }
                    }}
                    sx={{
                      minWidth: 'auto',
                      px: 2,
                      py: 0.5,
                      fontSize: '0.75rem'
                    }}
                  >
                    {showConcerningsPanel ? t('Hide') : t('Add')}
                  </Button>
                </Box>

                {/* Concernings List */}
                <Box sx={{
                  minHeight: 40,
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  p: 1.5,
                  backgroundColor: '#fafafa'
                }}>
                  {!dataLoaded ? (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      {t('Loading concernings...')}
                    </Typography>
                  ) : filterActiveConcernings(selectedConcernings, clients, companies).length > 0 ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {filterActiveConcernings(selectedConcernings, clients, companies).map((concerning, index) => (
                        <Chip
                          key={index}
                          label={getConcerningDisplayName(concerning)}
                          size="small"
                          color="warning"
                          variant="outlined"
                          onDelete={() => dispatch(removeSelectedConcerning(concerning))}
                        />
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      {t('No concernings selected')}
                    </Typography>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <DeactivatableField review={3} screen="factors" field="comment">
                  <InputField
                    error={Boolean(formik.touched.comment && formik.errors.comment)}
                    fullWidth
                    helperText={formik.touched.comment && formik.errors.comment}
                    label={t('Comments about this Factor')}
                    name="comment"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    type="comment"
                    value={formik.values.comment}
                    multiline
                    rows={2}
                  // sx={{'& .MuiFilledInput-root': {borderColor: 'red'}}}
                  />
                </DeactivatableField>
              </Grid>
              {/* Confidence level slot — 20260717-feat-factor-confidence-level */}
              <Grid item xs={12}>
                <DeactivatableField review={3} screen="factors" field="confidence_indice">
                  <ConfidenceTabItem
                    label={t('confidence_level')}
                    option="confidence_indice"
                    value={formik.values.confidence_indice || 1}
                    isActive={true}
                    onChangeField={(_, v) => formik.setFieldValue('confidence_indice', v)}
                  />
                </DeactivatableField>
              </Grid>
              {/* Top block save actions — persists the full current factor state. */}
              <Grid item xs={12}>
                <SectionSaveActions
                  onCancel={onClose}
                  onSave={() => { handleSubmitAndQuit(); }}
                  onSaveNew={() => { handleSubmitAndReset(); }}
                  disabled={saveDisabled}
                  saveLabel={saveLabel}
                />
              </Grid>

              <Grid item xs={12}>
                <CollapsibleSection
                  title={t('impact_of_this_factor')}
                  actions={
                    <SectionSaveActions
                      onCancel={onClose}
                      onSave={() => { handleSubmitAndQuit(); }}
                      onSaveNew={() => { handleSubmitAndReset(); }}
                      disabled={saveDisabled}
                      saveLabel={saveLabel}
                    />
                  }
                >
                  <Grid container spacing={2}>
                  <Grid
                    item
                    xs={12}
                    md={6}
                  >
                    <DeactivatableField review={3} screen="factors" field="pos_impact">
                      <Typography
                        variant='body1'
                      >
                        {t('positive_impact')}
                      </Typography>
                      <ToggleButtonGroup
                        value={formik.values.positiveImpact}
                        // onChange={formik.handleChange}
                        id="positiveImpact"
                        exclusive
                        fullWidth
                        size='small'
                        onChange={handlePositiveImpact}
                        aria-label="text alignment"
                      >
                        <ToggleButton value={-1} id="positiveImpact" aria-label="right aligned" sx={{ '&.Mui-selected': { backgroundColor: 'background.paper', color: 'text.primary' }, padding: 0.25, fontSize: "10px", borderTopLeftRadius: '100px', borderBottomLeftRadius: '100px' }}>
                          ?
                        </ToggleButton>
                        <ToggleButton value={0} id="positiveImpact" aria-label="left aligned" sx={{ color: '#49CC36', '&.Mui-selected': { backgroundColor: '#49CC36', color: '#FFF' }, padding: 0.25, fontSize: "10px" }}>
                          {/* <FormatAlignLeftIcon /> */}
                          0
                        </ToggleButton>
                        <ToggleButton value={1} id="positiveImpact" aria-label="left aligned" sx={{ color: '#A4E59B', '&.Mui-selected': { backgroundColor: '#A4E59B', color: '#FFF' }, padding: 0.25, fontSize: "10px" }}>
                          {/* <FormatAlignLeftIcon /> */}
                          +
                        </ToggleButton>
                        <ToggleButton value={2} id="positiveImpact" aria-label="centered" sx={{ color: '#77D968', '&.Mui-selected': { backgroundColor: '#77D968', color: '#FFF' }, padding: 0.25, fontSize: "10px" }}>
                          {/* <FormatAlignCenterIcon /> */}
                          + +
                        </ToggleButton>
                        <ToggleButton value={3} id="positiveImpact" aria-label="right aligned" sx={{ color: '#49CC36', '&.Mui-selected': { backgroundColor: '#49CC36', color: '#FFF' }, padding: 0.25, fontSize: "10px", borderTopRightRadius: '100px', borderBottomRightRadius: '100px' }}>
                          {/* <FormatAlignRightIcon /> */}
                          + + +
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </DeactivatableField>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}
                  >
                    <DeactivatableField review={3} screen="factors" field="neg_impact">
                      <Typography
                        variant='body1'
                      >
                        {t('negative_impact')}
                      </Typography>
                      <ToggleButtonGroup
                        value={formik.values.negativeImpact}
                        // onChange={formik.handleChange}
                        id="positiveImpact"
                        exclusive
                        fullWidth
                        size='small'
                        onChange={handleNegativeImpact}
                        aria-label="text alignment"
                      >
                        <ToggleButton value={-1} id="negativeImpact" aria-label="right aligned" sx={{ '&.Mui-selected': { backgroundColor: 'background.paper', color: 'text.primary' }, padding: 0.25, fontSize: "10px", borderTopLeftRadius: '100px', borderBottomLeftRadius: '100px' }}>
                          ?
                        </ToggleButton>
                        <ToggleButton value={0} id="negativeImpact" aria-label="left aligned" sx={{ color: '#FF4E33', '&.Mui-selected': { backgroundColor: '#FF4E33', color: '#FFF' }, padding: 0.25, fontSize: "10px" }}>
                          {/* <FormatAlignLeftIcon /> */}
                          0
                        </ToggleButton>
                        <ToggleButton value={1} id="negativeImpact" aria-label="left aligned" sx={{ color: '#FFA799', '&.Mui-selected': { backgroundColor: '#FFA799', color: '#FFF' }, padding: 0.25, fontSize: "10px" }}>
                          {/* <FormatAlignLeftIcon /> */}
                          -
                        </ToggleButton>
                        <ToggleButton value={2} id="negativeImpact" aria-label="centered" sx={{ color: '#FF7A66', '&.Mui-selected': { backgroundColor: '#FF7A66', color: '#FFF' }, padding: 0.25, fontSize: "10px" }}>
                          {/* <FormatAlignCenterIcon /> */}
                          - -
                        </ToggleButton>
                        <ToggleButton value={3} id="negativeImpact" aria-label="right aligned" sx={{ color: '#FF4E33', '&.Mui-selected': { backgroundColor: '#FF4E33', color: '#FFF' }, padding: 0.25, fontSize: "10px", borderTopRightRadius: '100px', borderBottomRightRadius: '100px' }}>
                          {/* <FormatAlignRightIcon /> */}
                          - - -
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </DeactivatableField>
                  </Grid>
                  {/* <Grid
                    item
                    xs={12}
                    md={6}
                  >
                    <FormGroup>
                      <FormControlLabel control={<Checkbox checked={formik.values.opportunityKiller} onChange={handleOpportunityKiller} />} label={t('opportunity_killer')} />
                    </FormGroup>
                  </Grid> */}
                  <Grid
                    item
                    xs={12}
                    md={6}
                  >
                    <DeactivatableField review={3} screen="factors" field="opportunity_killer">
                      <Typography variant='body1'>
                        {t('importance_of_this_factor')}
                      </Typography>
                      <FormControl fullWidth>
                        <Select
                          value={formik.values.opportunityKiller}
                          onChange={(e) => formik.setFieldValue('opportunityKiller', Number(e.target.value))}
                          variant="standard"
                          disableUnderline
                          sx={{
                            backgroundColor: importanceStatus[formik.values.opportunityKiller]?.['backgroundColor'] || 'rgba(138, 138, 138, 0.05)',
                            color: importanceStatus[formik.values.opportunityKiller]?.['color'] || '#8A8A8A',
                            py: 0.15, px: 1, borderRadius: 1, fontSize: 14, fontWeight: 500
                          }}
                          IconComponent={(props) => (<ChevronDownIcon {...props} sx={{ color: `${importanceStatus[formik.values.opportunityKiller]?.['color'] || '#8A8A8A'} !important` }} />)}
                        >
                          {Object.keys(importanceStatus).map((importance) => (
                            <MenuItem key={importance} value={Number(importance)}>{t(importanceStatus[importance]['label'])}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </DeactivatableField>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}
                  >
                    <DeactivatableField review={3} screen="factors" field="probability">
                      <InputField
                        error={Boolean(formik.touched.probability && formik.errors.probability)}
                        fullWidth
                        helperText={formik.touched.probability && formik.errors.probability}
                        label={t('probability')}
                        name="probability"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        required
                        type="probability"
                        value={formik.values.probability}
                        select
                      >
                        <MenuItem value={1} sx={{ color: '#27AB6E', fontWeight: 600 }}>{t('low')}</MenuItem>
                        <MenuItem value={2} sx={{ color: '#FF7A00', fontWeight: 600 }}>{t('medium')}</MenuItem>
                        <MenuItem value={3} sx={{ color: '#FF2200', fontWeight: 600 }}>{t('high')}</MenuItem>
                        <MenuItem value={4} sx={{ color: '#B00020', fontWeight: 600 }}>{t('for_sure')}</MenuItem>
                        <MenuItem value={0}>?</MenuItem>
                      </InputField>
                    </DeactivatableField>
                  </Grid>
                  </Grid>
                </CollapsibleSection>
              </Grid>

              <Grid item xs={12}>
                <CollapsibleSection
                  title={t('mitigation_of_this_factor')}
                  actions={
                    <SectionSaveActions
                      onCancel={onClose}
                      onSave={() => { handleSubmitAndQuit(); }}
                      onSaveNew={() => { handleSubmitAndReset(); }}
                      disabled={saveDisabled}
                      saveLabel={saveLabel}
                    />
                  }
                >
                  <Grid container spacing={2}>
                  {/* Actions Section Header */}
                  <Grid item xs={12}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 2
                    }}>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {t('Actions')}
                      </Typography>
                      <InfoPop
                        infoElementName={t('actions')}
                        shortText='What are Actions?'
                        position='right'
                        longText='Actions are tactical moves taken to address Factors. Preventive Actions are actions taken proactively to prevent or mitigate negative Factors, while Contingency Actions are actions taken reactively if a Factor occurs. Actions can be selected from existing actions or created dynamically.'
                      />
                    </Box>
                  </Grid>

                  {/* Preventive Actions Section */}
                  <Grid item xs={12}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 1
                    }}>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {t('Actions to Prevent this Risk from Happening')}
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          if (showPreventiveMovesPanel) {
                            setShowPreventiveMovesPanel(false);
                          } else {
                            setShowConcerningsPanel(false);
                            setShowContingencyMovesPanel(false);
                            setShowBoosterMovesPanel(false);
                            setShowPreventiveMovesPanel(true);
                          }
                        }}
                        sx={{
                          minWidth: 'auto',
                          px: 2,
                          py: 0.5,
                          fontSize: '0.75rem'
                        }}
                      >
                        {showPreventiveMovesPanel ? t('Hide') : t('Add')}
                      </Button>
                    </Box>

                    {/* Preventive Actions List */}
                    <Box sx={{
                      minHeight: 40,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      p: 1.5,
                      backgroundColor: '#fafafa'
                    }}>
                      {selectedPreventiveMoves.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selectedPreventiveMoves.map((move) => (
                            <Chip
                              key={move.id}
                              label={move.name}
                              size="small"
                              onDelete={() => dispatch(removeSelectedPreventiveMove(move.id))}
                              color="warning"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          {t('No preventive actions selected')}
                        </Typography>
                      )}
                    </Box>
                  </Grid>

                  {/* Contingency Actions Section */}
                  <Grid item xs={12}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 1
                    }}>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {t('Actions Addressing Risk when it Materializes')}
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          if (showContingencyMovesPanel) {
                            setShowContingencyMovesPanel(false);
                          } else {
                            setShowConcerningsPanel(false);
                            setShowPreventiveMovesPanel(false);
                            setShowBoosterMovesPanel(false);
                            setShowContingencyMovesPanel(true);
                          }
                        }}
                        sx={{
                          minWidth: 'auto',
                          px: 2,
                          py: 0.5,
                          fontSize: '0.75rem'
                        }}
                      >
                        {showContingencyMovesPanel ? t('Hide') : t('Add')}
                      </Button>
                    </Box>

                    {/* Contingency Actions List */}
                    <Box sx={{
                      minHeight: 40,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      p: 1.5,
                      backgroundColor: '#fafafa'
                    }}>
                      {selectedContingencyMoves.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selectedContingencyMoves.map((move) => (
                            <Chip
                              key={move.id}
                              label={move.name}
                              size="small"
                              onDelete={() => dispatch(removeSelectedContingencyMove(move.id))}
                              color="warning"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          {t('No contingency actions selected')}
                        </Typography>
                      )}
                    </Box>
                  </Grid>

                  {/* Booster Actions Section */}
                  <Grid item xs={12}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 1
                    }}>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {t('Actions to Take Advantage of a Booster (Positive Factor)')}
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          if (showBoosterMovesPanel) {
                            setShowBoosterMovesPanel(false);
                          } else {
                            setShowConcerningsPanel(false);
                            setShowPreventiveMovesPanel(false);
                            setShowContingencyMovesPanel(false);
                            setShowBoosterMovesPanel(true);
                          }
                        }}
                        sx={{
                          minWidth: 'auto',
                          px: 2,
                          py: 0.5,
                          fontSize: '0.75rem'
                        }}
                      >
                        {showBoosterMovesPanel ? t('Hide') : t('Add')}
                      </Button>
                    </Box>

                    {/* Booster Actions List */}
                    <Box sx={{
                      minHeight: 40,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      p: 1.5,
                      backgroundColor: '#fafafa'
                    }}>
                      {selectedBoosterMoves.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selectedBoosterMoves.map((move) => (
                            <Chip
                              key={move.id}
                              label={move.name}
                              size="small"
                              onDelete={() => dispatch(removeSelectedBoosterMove(move.id))}
                              color="warning"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          {t('No booster actions selected')}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                  {formik.errors.submit && (
                    <Grid
                      item
                      xs={12}
                    >
                      <FormHelperText error>
                        {formik.errors.submit}
                      </FormHelperText>
                    </Grid>
                  )}
                  </Grid>
                </CollapsibleSection>
              </Grid>
            </Grid>
          </Grid>
          {showList && !showConcerningsPanel && !showPreventiveMovesPanel && !showContingencyMovesPanel && !showBoosterMovesPanel && (
            <Grid item xs={4} sx={{ backgroundColor: "rgba(255, 123, 0, 0.25)", overflowY: "scroll" }} padding={0} spacing={2} height={height}>
              <Grid container spacing={2} padding={4} sx={{ paddingLeft: 2 }}>
                <Grid item xs={12} sx={{ paddingLeft: 0 }}>
                  <Typography variant="h5" style={{ color: 'rgba(255, 123, 0, 1)' }}>
                    All factors <Chip color='default' label={total} size='small' variant='filled' />
                  </Typography>
                </Grid>
                <Grid item xs={12} sx={{ paddingLeft: 0 }}>
                  <Button variant='contained' size='small' color='warning' fullWidth onClick={() => dispatch(openFactorDialog({ element: { id: null }, isEditabled: true, isDuplicated: false }))}>
                    New factor
                  </Button>
                </Grid>
                <Grid item xs={12} sx={{ paddingLeft: 0 }}>
                  <InputField
                    fullWidth
                    placeholder='Search for a factor'
                    name="search"
                    onChange={(e) => setSearchedString(e.target.value)}
                    type="text"
                    value={searchedString}
                  />
                </Grid>
                <Grid item xs={12}>
                  <List>
                    {items?.map((u) => (
                      <ListItem disablePadding sx={{ backgroundColor: "background.default", marginBottom: 1, borderRadius: 1, padding: 0, fontSize: "10px" }}>
                        <ButtonGroup
                          variant="contained"
                          ref={anchorRef}
                          aria-label="Button group with a nested menu"
                          fullWidth
                          sx={{ padding: 0, fontSize: "10px", backgroundColor: 'white' }}
                          color='inherit'
                        >
                          <Button onClick={() => dispatch(openFactorDialog({ element: u, isEditabled: true, isDuplicated: false }))} sx={{ backgroundColor: 'white', padding: '10px', textAlign: 'left' }}><span style={{ padding: '10px', width: '100%' }}>{u.description}</span></Button>
                          <Button
                            size="small"
                            aria-controls={open ? 'split-button-menu' : undefined}
                            aria-expanded={open ? 'true' : undefined}
                            aria-label="select merge strategy"
                            aria-haspopup="menu"
                            style={{ width: '1%' }}
                            sx={{ backgroundColor: 'white' }}
                          // onClick={handleToggle}
                          >
                            {/* <FontAwesomeIcon icon={faEllipsis as IconProp} /> */}
                            <TableItemMenu
                              onView={() => dispatch(openFactorDialog({ element: u, isEditabled: false, isDuplicated: false }))}
                              onEdit={() => dispatch(openFactorDialog({ element: u, isEditabled: true, isDuplicated: false }))}
                              onDuplicate={() => dispatch(openFactorDialog({ element: u, isEditabled: true, isDuplicated: true }))}
                              onDelete={async () => {
                                await dispatch(factorDelete(u.id));
                                getItems();
                              }}
                            />
                          </Button>
                        </ButtonGroup>
                      </ListItem>
                    ))}
                  </List>
                </Grid>
              </Grid>
            </Grid>
          )}
          {showList && showConcerningsPanel && (
            <FactorConcerningsPanel height={height} onHide={() => setShowConcerningsPanel(false)} />
          )}
          {showList && showPreventiveMovesPanel && (
            <FactorPreventiveMovesPanel height={height} onHide={() => setShowPreventiveMovesPanel(false)} />
          )}
          {showList && showContingencyMovesPanel && (
            <FactorContingencyMovesPanel height={height} onHide={() => setShowContingencyMovesPanel(false)} />
          )}
          {showList && showBoosterMovesPanel && (
            <FactorBoosterMovesPanel height={height} onHide={() => setShowBoosterMovesPanel(false)} />
          )}
        </Grid>
      </DialogContent>
      <Divider />
      {/* <DialogActions>
        <Button
          color="primary"
          size="small"
          onClick={onClose}
          variant="outlined"
        >
          {t('cancel')}
        </Button>
        <Button
          color="success"
          size="small"
          disabled={(factor.factorIsLoading || !factor.isEditabled)}
          onClick={() => { handleSubmitAndQuit(); }}
          variant="contained"
        >
          {(factor.factorId !== null && factor.factorId !== undefined) ? t('update') : t('create')}
        </Button>
      </DialogActions> */}
      <ConfirmationDialog
        message={t('do_you_want_to_close_without_saving')}
        onCancel={() => { setOpenConfirmationDialog(false) }}
        onConfirm={() => {
          setOpenConfirmationDialog(false);
          onClose();
        }}
        open={openConfirmationDialog}
        title={t('confirmation_title')}
        variant='warning'
      />
    </Dialog>
  );
};

FactorDialog.defaultProps = {
  open: false
};

FactorDialog.propTypes = {
  // @ts-ignore
  open: PropTypes.bool,
  onClose: PropTypes.func
};
